import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { supabase } from '../lib/supabase_client';

export interface InvoiceTransactionData {
    id: string;
    created_at: string;
    amount: number;
    credits: number;
    products: {
        name: string;
    };
    sequential_invoice_number?: number;
}

export interface InvoiceCustomerData {
    company_name: string;
    billing_address: string;
    contact_phone?: string;
    contact_email?: string;
}

interface InvoiceSellerInfo {
    companyName: string;
    registrationNumber: string;
    address: string;
    phone: string;
    email: string;
    sstRegNo?: string;
}

/**
 * Helper function to draw company header
 */
const drawCompanyHeader = (
    pdf: jsPDF,
    margin: number,
    yPos: number,
    companyInfo: InvoiceSellerInfo
): number => {
    // Company name 
    pdf.setFont('times', 'bold');
    pdf.setFontSize(14);
    pdf.text(companyInfo.companyName, margin, yPos);

    // Registration number
    const companyNameWidth = pdf.getTextWidth(companyInfo.companyName);
    pdf.setFont('times', 'normal');
    pdf.setFontSize(10);
    pdf.text(` ${companyInfo.registrationNumber}`, margin + companyNameWidth, yPos);

    // Company details 
    yPos += 5;
    pdf.setFontSize(12);
    pdf.text(companyInfo.address, margin, yPos);
    yPos += 5;
    pdf.text(`Phone: ${companyInfo.phone}`, margin, yPos);
    yPos += 5;
    pdf.text(`Email: ${companyInfo.email}`, margin, yPos);
    
    // SST Reg No (only for SST invoice)
    if (companyInfo.sstRegNo) {
        yPos += 5;
        pdf.text(`SST Reg No: ${companyInfo.sstRegNo}`, margin, yPos);
    }

    return yPos;
};

/**
 * Helper function to draw Bill To section
 */
const drawBillToSection = (
    pdf: jsPDF,
    margin: number,
    yPos: number,
    customerData: InvoiceCustomerData
): number => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const maxTextWidth = pageWidth - margin * 2; // Max width for text wrapping

    // Bill To section 
    pdf.setFont('times', 'normal');
    pdf.setFontSize(12);
    pdf.text("Bill To:", margin, yPos);

    // Company name  
    yPos += 10;
    pdf.setFont('times', 'bold');
    // Wrap company name if too long
    const companyLines = pdf.splitTextToSize(customerData.company_name, maxTextWidth);
    companyLines.forEach((line: string) => {
        pdf.text(line, margin, yPos);
        yPos += 5;
    });

    // Billing address - handle both newline-separated and long continuous addresses
    pdf.setFont('times', 'normal');
    if (customerData.billing_address) {
        // First split by newlines
        const addressLines = customerData.billing_address.split('\n');
        addressLines.forEach((line: string) => {
            if (line.trim()) {
                // Then wrap each line if it's too long
                const wrappedLines = pdf.splitTextToSize(line.trim(), maxTextWidth);
                wrappedLines.forEach((wrappedLine: string) => {
                    pdf.text(wrappedLine, margin, yPos);
                    yPos += 5;
                });
            }
        });
    }

    // Contact details
    if (customerData.contact_phone) {
        yPos += 5;
        pdf.text(`Phone: ${customerData.contact_phone}`, margin, yPos);
    }
    if (customerData.contact_email) {
        yPos += 5;
        pdf.text(`Email: ${customerData.contact_email}`, margin, yPos);
    }

    return yPos;
};

/**
 * Helper function to draw invoice details (Date, Invoice No, Ref No)
 */
const drawInvoiceDetails = (
    pdf: jsPDF,
    pageWidth: number,
    margin: number,
    yPos: number,
    formattedDate: string,
    invoiceId: string,
    includeRefNo: boolean = false,
    refNo?: string
): number => {
    const rightMargin = pageWidth - margin;
    pdf.setFont('times', 'normal');
    pdf.setFontSize(12);
    
    // Date below email
    let invoiceDetailsY = yPos + 5;
    pdf.text(`Date : ${formattedDate}`, rightMargin, invoiceDetailsY, { align: 'right' });
    
    // Invoice No 
    invoiceDetailsY += 5;
    pdf.text(`Invoice No : ${invoiceId}`, rightMargin, invoiceDetailsY, { align: 'right' });
    
    // Ref No below Invoice No (only for SST invoice)
    if (includeRefNo && refNo) {
        invoiceDetailsY += 5;
        pdf.text(`Ref No: ${refNo}`, rightMargin, invoiceDetailsY, { align: 'right' });
    }

    return invoiceDetailsY;
};

/**
 * Helper function to draw table
 */
const drawTable = (
    pdf: jsPDF,
    margin: number,
    tableY: number,
    tableData: string[][],
    colWidths: number[]
): void => {
    const totalWidth = colWidths.reduce((sum, width) => sum + width, 0);

    tableData.forEach((row, rowIndex) => {
        let xPos = margin;
        // Header row 
        const rowHeight = rowIndex === 0 ? 10 : 8;
        const currentRowY = tableY;

        // Set border properties 
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.5);
        
        // Draw cells with background fill
        row.forEach((cell, colIndex) => {
            const cellWidth = colWidths[colIndex];
            
            // Set fill color based on row type
            if (rowIndex === 0) {
                // Header row styling
                pdf.setFillColor(128, 128, 128); 
                pdf.rect(xPos, currentRowY, cellWidth, rowHeight, 'FD');
                pdf.setTextColor(245, 245, 245); 
                pdf.setFont('times', 'bold');
                pdf.setFontSize(12);
            } else if (rowIndex === tableData.length - 1) {
                // Total row styling
                pdf.setFillColor(245, 245, 220); 
                pdf.rect(xPos, currentRowY, cellWidth, rowHeight, 'FD');
                pdf.setTextColor(0, 0, 0);
                pdf.setFont('times', 'bold');
                pdf.setFontSize(12);
            } else if (rowIndex === tableData.length - 2 && tableData.length > 2) {
                // SST row styling
                pdf.setFillColor(211, 211, 211); 
                pdf.rect(xPos, currentRowY, cellWidth, rowHeight, 'FD');
                pdf.setTextColor(0, 0, 0);
                pdf.setFont('times', 'normal');
                pdf.setFontSize(12);
            } else {
                // Regular row 
                pdf.setFillColor(255, 255, 255);
                pdf.rect(xPos, currentRowY, cellWidth, rowHeight, 'FD');
                pdf.setTextColor(0, 0, 0);
                pdf.setFont('times', 'normal');
                pdf.setFontSize(12);
            }
            
            // Draw text 
            const textY = currentRowY + (rowHeight / 2) + 2;
            
            // Text alignment:
            let textX: number;
            let align: 'left' | 'center' | 'right';
            
            if (colIndex === 0) {
                // Description 
                textX = xPos + 2;
                align = 'left';
            } else if (colIndex === 1) {
                // Credits
                textX = xPos + cellWidth / 2;
                align = 'center';
            } else {
                // Amount 
                textX = xPos + cellWidth - 2;
                align = 'right';
            }
            
            pdf.text(cell, textX, textY, { align });
            
            xPos += cellWidth;
        });

        tableY += rowHeight;
    });
};

/**
 * Fetch company seller info from database based on invoice version
 */
const fetchSellerInfo = async (invoiceVersion: number): Promise<InvoiceSellerInfo> => {
    const { data, error } = await supabase
        .from('seller_info')
        .select('*')
        .eq('invoice_version', invoiceVersion)
        .maybeSingle();

    if (error || !data) {
      throw new Error('Error fetching company info');
    }

    return {
        companyName: data.company_name,
        registrationNumber: data.registration_number,
        address: data.address,
        phone: data.phone,
        email: data.email,
        sstRegNo: data.sst_reg_no || undefined
    };
};

/**
 * Generate SST invoice and return as blob (Version 3)
 */
export const generateSSTInvoice = async (
    transactionData: InvoiceTransactionData,
    customerData: InvoiceCustomerData
): Promise<Blob> => {
    try {
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        let yPos = 28;

        // Fetch company info from database
        const companyInfo = await fetchSellerInfo(3);

        yPos = drawCompanyHeader(pdf, margin, yPos, companyInfo);

        // INVOICE heading
        yPos += 10;
        pdf.setFont('times', 'bold');
        pdf.setFontSize(16);
        pdf.text("Invoice", margin, yPos);

        // Separator line
        yPos += 3;
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPos, pageWidth - margin, yPos);

        // Bill To section
        yPos += 7;
        yPos = drawBillToSection(pdf, margin, yPos, customerData);

        // Format date (DD-MM-YYYY)
        const createdDate = new Date(transactionData.created_at);
        const formattedDate = createdDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '-');

        // Invoice ID - sequential number format
        const sequentialNumber = transactionData.sequential_invoice_number || 862;
        const invoiceId = `INV${sequentialNumber.toString().padStart(6, '0')}`;

        // Ref No - from transaction ID
        let refNo = transactionData.id;
        if (refNo.includes('-')) {
            refNo = refNo.split('-')[0];
        } else {
            refNo = refNo.substring(0, 8);
        }

        // SST calculation
        const totalInclusive = parseFloat(transactionData.amount.toString());
        const sstRate = 8.00;
        const subtotal = totalInclusive / (1 + sstRate / 100);
        const sstAmount = totalInclusive - subtotal;

        // Invoice details with Ref No
        const invoiceDetailsY = drawInvoiceDetails(pdf, pageWidth, margin, yPos, formattedDate, invoiceId, true, refNo);

        // Table position
        const tableHeaderY = invoiceDetailsY + 10;
        let tableY = tableHeaderY;

        // Table data with SST breakdown
        const tableData = [
            ['Description', 'Credits', 'Amount (RM)'],
            [transactionData.products.name, transactionData.credits.toString(), `RM${subtotal.toFixed(2)}`],
            [`SST (${sstRate.toFixed(1)}%)`, '', `RM${sstAmount.toFixed(2)}`],
            ['Total (Inclusive of SST)', '', `RM${totalInclusive.toFixed(2)}`]
        ];

        const totalWidth = 162;
        const colWidths = [
            totalWidth * 0.59,
            totalWidth * 0.235,
            totalWidth * 0.235
        ];

        drawTable(pdf, margin, tableY, tableData, colWidths);

        // Footer with SST info
        pdf.setFont('times', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(0, 0, 0);
        pdf.text("This is a computer generated invoice. No signature is required.", margin, pageHeight - 20);
        pdf.text("Prices inclusive of 8% Sales and Service Tax (SST)", margin, pageHeight - 15);

        // Return PDF as blob
        return pdf.output('blob');

    } catch (error) {
        console.error('Error generating SST invoice blob:', error);
        throw new Error('Failed to generate SST invoice PDF');
    }
};

/**
 * Generate regular invoice and return as blob (Version 1 & 2) - No SST
 */
export const generateRegularInvoice = async (
    transactionData: InvoiceTransactionData,
    customerData: InvoiceCustomerData
): Promise<Blob> => {
    try {
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        let yPos = 28;

        // Fetch company info from database
        const companyInfo = await fetchSellerInfo(2);

        yPos = drawCompanyHeader(pdf, margin, yPos, companyInfo);

        // INVOICE heading
        yPos += 10;
        pdf.setFont('times', 'bold');
        pdf.setFontSize(16);
        pdf.text("Invoice", margin, yPos);

        // Separator line
        yPos += 3;
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPos, pageWidth - margin, yPos);

        // Bill To section
        yPos += 7;
        yPos = drawBillToSection(pdf, margin, yPos, customerData);

        // Format date (DD-MM-YYYY)
        const createdDate = new Date(transactionData.created_at);
        const formattedDate = createdDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '-');

        // Invoice ID - from transaction ID (not sequential)
        let invoiceId = transactionData.id;
        if (invoiceId.includes('-')) {
            invoiceId = invoiceId.split('-')[0];
        } else {
            invoiceId = invoiceId.substring(0, 8);
        }

        // Invoice details without Ref No
        const invoiceDetailsY = drawInvoiceDetails(pdf, pageWidth, margin, yPos, formattedDate, invoiceId, false);

        // Table position
        const tableHeaderY = invoiceDetailsY + 10;
        let tableY = tableHeaderY;

        // Table data (no SST breakdown)
        const tableData = [
            ['Description', 'Credits', 'Amount (RM)'],
            [transactionData.products.name, transactionData.credits.toString(), `RM${transactionData.amount.toFixed(2)}`],
            ['Total', '', `RM${transactionData.amount.toFixed(2)}`]
        ];

        const totalWidth = 162;
        const colWidths = [
            totalWidth * 0.59,
            totalWidth * 0.235,
            totalWidth * 0.235
        ];

        drawTable(pdf, margin, tableY, tableData, colWidths);

        // Footer
        pdf.setFont('times', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(0, 0, 0);
        pdf.text("This is a computer generated invoice. No signature is required.", margin, pageHeight - 20);

        // Return PDF as blob
        return pdf.output('blob');

    } catch (error) {
        console.error('Error generating regular invoice blob:', error);
        throw new Error('Failed to generate regular invoice PDF');
    }
};

/**
 * Generate Singapore invoice and return as blob (Version 4) - No SST, SGD
 * Same layout as version 2 but uses seller_info for version 4 and SGD currency.
 */
export const generateSingaporeInvoice = async (
    transactionData: InvoiceTransactionData,
    customerData: InvoiceCustomerData
): Promise<Blob> => {
    try {
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        let yPos = 28;

        // Fetch company info from database (version 4 = Singapore seller)
        const companyInfo = await fetchSellerInfo(4);

        yPos = drawCompanyHeader(pdf, margin, yPos, companyInfo);

        // INVOICE heading
        yPos += 10;
        pdf.setFont('times', 'bold');
        pdf.setFontSize(16);
        pdf.text("Invoice", margin, yPos);

        // Separator line
        yPos += 3;
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPos, pageWidth - margin, yPos);

        // Bill To section
        yPos += 7;
        yPos = drawBillToSection(pdf, margin, yPos, customerData);

        // Format date (DD-MM-YYYY)
        const createdDate = new Date(transactionData.created_at);
        const formattedDate = createdDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '-');

        // Invoice ID - from transaction ID (version 4 has no sequential number)
        let invoiceId = transactionData.id;
        if (invoiceId.includes('-')) {
            invoiceId = invoiceId.split('-')[0];
        } else {
            invoiceId = invoiceId.substring(0, 8);
        }

        // Invoice details without Ref No
        const invoiceDetailsY = drawInvoiceDetails(pdf, pageWidth, margin, yPos, formattedDate, invoiceId, false);

        // Table position
        const tableHeaderY = invoiceDetailsY + 10;
        let tableY = tableHeaderY;

        // Table data (no SST, SGD)
        const tableData = [
            ['Description', 'Credits', 'Amount (SGD)'],
            [transactionData.products.name, transactionData.credits.toString(), `SGD ${transactionData.amount.toFixed(2)}`],
            ['Total', '', `SGD ${transactionData.amount.toFixed(2)}`]
        ];

        const totalWidth = 162;
        const colWidths = [
            totalWidth * 0.59,
            totalWidth * 0.235,
            totalWidth * 0.235
        ];

        drawTable(pdf, margin, tableY, tableData, colWidths);

        // Footer
        pdf.setFont('times', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(0, 0, 0);
        pdf.text("This is a computer generated invoice. No signature is required.", margin, pageHeight - 20);

        return pdf.output('blob');

    } catch (error) {
        console.error('Error generating Singapore invoice blob:', error);
        throw new Error('Failed to generate Singapore invoice PDF');
    }
};

/**
 * Generate old invoice and return as blob (Version 1) - Old company format
 */
export const generateOldInvoice = async (
    transactionData: InvoiceTransactionData,
    customerData: InvoiceCustomerData
): Promise<Blob> => {
    try {
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        let yPos = 28;

        // Fetch company info from database
        const companyInfo = await fetchSellerInfo(1);

        yPos = drawCompanyHeader(pdf, margin, yPos, companyInfo);

        // INVOICE heading
        yPos += 10;
        pdf.setFont('times', 'bold');
        pdf.setFontSize(16);
        pdf.text("Invoice", margin, yPos);

        // Separator line
        yPos += 3;
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPos, pageWidth - margin, yPos);

        // Bill To section
        yPos += 7;
        yPos = drawBillToSection(pdf, margin, yPos, customerData);

        // Format date (DD-MM-YYYY)
        const createdDate = new Date(transactionData.created_at);
        const formattedDate = createdDate.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '-');

        // Invoice ID - from transaction ID (not sequential)
        let invoiceId = transactionData.id;
        if (invoiceId.includes('-')) {
            invoiceId = invoiceId.split('-')[0];
        } else {
            invoiceId = invoiceId.substring(0, 8);
        }

        // Invoice details without Ref No
        const invoiceDetailsY = drawInvoiceDetails(pdf, pageWidth, margin, yPos, formattedDate, invoiceId, false);

        // Table position
        const tableHeaderY = invoiceDetailsY + 10;
        let tableY = tableHeaderY;

        // Table data (no SST breakdown)
        const tableData = [
            ['Description', 'Credits', 'Amount (RM)'],
            [transactionData.products.name, transactionData.credits.toString(), `RM${transactionData.amount.toFixed(2)}`],
            ['Total', '', `RM${transactionData.amount.toFixed(2)}`]
        ];

        const totalWidth = 162;
        const colWidths = [
            totalWidth * 0.59,
            totalWidth * 0.235,
            totalWidth * 0.235
        ];

        drawTable(pdf, margin, tableY, tableData, colWidths);

        // Footer
        pdf.setFont('times', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(0, 0, 0);
        pdf.text("This is a computer generated invoice. No signature is required.", margin, pageHeight - 20);

        // Return PDF as blob
        return pdf.output('blob');

    } catch (error) {
        console.error('Error generating old invoice blob:', error);
        throw new Error('Failed to generate old invoice PDF');
    }
};

// ---------------------------------------------------------------------------
// UBL JSON helpers — LHDN format is [{ _: value }] arrays throughout
// ---------------------------------------------------------------------------
type UblArr = Array<{ _?: unknown }> | undefined;
const g = (arr: UblArr): string => String(arr?.[0]?._ ?? '');
const gNum = (arr: UblArr): number => Number(arr?.[0]?._ ?? 0);
type UblId = Array<{ ID?: Array<{ _?: string; schemeID?: string }> }> | undefined;
const getPartyId = (ids: UblId, scheme: string): string =>
    ids?.find(p => p.ID?.[0]?.schemeID === scheme)?.ID?.[0]?._ ?? 'N/A';
type UblAddrLine = Array<{ Line?: Array<{ _?: unknown }> }> | undefined;
const getAddressLines = (lines: UblAddrLine): string[] =>
    (lines ?? []).map(l => String(l.Line?.[0]?._ ?? '')).filter(Boolean);
const fmtAmt = (n: number): string =>
    n.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Generate a Malaysian e-Invoice PDF from a stored LHDN UBL JSON payload.
 * Includes a QR code linking to the LHDN validation / share URL.
 */
export const generateEInvoiceFromPayload = async (
    payload: Record<string, unknown>,
    documentUuid: string,
    longId: string,
    submittedAt?: string | null
): Promise<Blob> => {
    // ---- Extract UBL data ----
    // The LHDN UBL payload is an opaque JSON blob — we cast through unknown to avoid excessive typing
    const inv = (payload.Invoice as unknown[])[0] as Record<string, unknown>;
    const supplierParty = ((inv.AccountingSupplierParty as unknown[])[0]) as Record<string, unknown>;
    const supplier = ((supplierParty.Party as unknown[])[0]) as Record<string, unknown>;
    const buyer = (((inv.AccountingCustomerParty as unknown[])[0]) as Record<string, unknown>).Party as unknown[];
    const buyerParty = buyer[0] as Record<string, unknown>;
    const monetary = ((inv.LegalMonetaryTotal as unknown[])[0]) as Record<string, unknown>;
    const taxTotal = (inv.TaxTotal as unknown[])?.[0] as Record<string, unknown> | undefined;

    const invoiceId  = g(inv.ID as UblArr);
    const issueDate  = g(inv.IssueDate as UblArr);
    const currency   = g(inv.DocumentCurrencyCode as UblArr);

    const sellerName  = g((supplier.PartyLegalEntity as {RegistrationName?: UblArr}[])?.[0]?.RegistrationName);
    const sellerTin   = getPartyId(supplier.PartyIdentification as UblId, 'TIN');
    const sellerBrn   = getPartyId(supplier.PartyIdentification as UblId, 'BRN') !== 'N/A'
        ? getPartyId(supplier.PartyIdentification as UblId, 'BRN')
        : getPartyId(supplier.PartyIdentification as UblId, 'NRIC');
    const sellerSst   = getPartyId(supplier.PartyIdentification as UblId, 'SST');
    const sellerAddrRaw = (supplier.PostalAddress as {CityName?: UblArr; PostalZone?: UblArr; AddressLine?: UblAddrLine}[])?.[0];
    const sellerLines = getAddressLines(sellerAddrRaw?.AddressLine);
    const sellerCity  = g(sellerAddrRaw?.CityName);
    const sellerPostal = g(sellerAddrRaw?.PostalZone);
    const sellerContact = (supplier.Contact as {Telephone?: UblArr; ElectronicMail?: UblArr}[])?.[0];
    const sellerPhone = sellerContact ? g(sellerContact.Telephone) : '';
    const sellerEmail = sellerContact ? g(sellerContact.ElectronicMail) : '';
    const msicArr = supplier.IndustryClassificationCode as Array<{_?: unknown; name?: string}> | undefined;
    const sellerMsic  = msicArr?.[0] ? `${msicArr[0]._ ?? ''} – ${msicArr[0].name ?? ''}` : '';

    const buyerName   = g((buyerParty.PartyLegalEntity as {RegistrationName?: UblArr}[])?.[0]?.RegistrationName);
    const buyerTin    = getPartyId(buyerParty.PartyIdentification as UblId, 'TIN');
    const buyerIdSchemes = ['BRN', 'NRIC', 'PASSPORT', 'ARMY'];
    const buyerIdScheme = buyerIdSchemes.find(s => getPartyId(buyerParty.PartyIdentification as UblId, s) !== 'N/A') ?? 'BRN';
    const buyerIdVal  = getPartyId(buyerParty.PartyIdentification as UblId, buyerIdScheme);
    const buyerSst    = getPartyId(buyerParty.PartyIdentification as UblId, 'SST');
    const buyerAddrRaw = (buyerParty.PostalAddress as {CityName?: UblArr; PostalZone?: UblArr; AddressLine?: UblAddrLine}[])?.[0];
    const buyerLines  = getAddressLines(buyerAddrRaw?.AddressLine);
    const buyerCity   = g(buyerAddrRaw?.CityName);
    const buyerPostal = g(buyerAddrRaw?.PostalZone);
    const buyerContact = (buyerParty.Contact as {Telephone?: UblArr; ElectronicMail?: UblArr}[])?.[0];
    const buyerPhone  = buyerContact ? g(buyerContact.Telephone) : '';
    const buyerEmail  = buyerContact ? g(buyerContact.ElectronicMail) : '';

    const invoiceLines = (inv.InvoiceLine as Record<string, unknown>[]) ?? [];
    const subtotal   = gNum(monetary.TaxExclusiveAmount as UblArr);
    const taxAmount  = gNum(taxTotal?.TaxAmount as UblArr);
    const total      = gNum(monetary.PayableAmount as UblArr);
    const taxSubtotal0 = (taxTotal?.TaxSubtotal as UblArr)?.[0] as Record<string, unknown> | undefined;
    const taxPct     = (taxSubtotal0?.Percent as UblArr)?.[0]?._ ?? 0;

    const validationUrl = `https://myinvois.hasil.gov.my/${documentUuid}/share/${longId}`;

    // ---- QR code ----
    const qrDataUrl = await QRCode.toDataURL(validationUrl, { width: 160, margin: 1 });

    // ---- Build PDF ----
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth  = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    let y = margin;

    // ── Header bar ──
    pdf.setFillColor(13, 110, 190);
    pdf.rect(0, 0, pageWidth, 12, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(255, 255, 255);
    pdf.text('LHDN MyInvois', margin, 8);
    pdf.text('VALID', pageWidth - margin - pdf.getTextWidth('VALID'), 8);
    pdf.setTextColor(0, 0, 0);

    y = 28;

    // ── Title + Invoice ID ──
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.text('e-Invoice', margin, y);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(80, 80, 80);
    const idLabel = `Invoice No: ${invoiceId}`;
    pdf.text(idLabel, pageWidth - margin - pdf.getTextWidth(idLabel), y);
    const dateLabel = `Date: ${issueDate}`;
    pdf.text(dateLabel, pageWidth - margin - pdf.getTextWidth(dateLabel), y + 5);
    if (submittedAt) {
        const subLabel = `Submitted: ${new Date(submittedAt).toLocaleDateString('en-MY')}`;
        pdf.text(subLabel, pageWidth - margin - pdf.getTextWidth(subLabel), y + 10);
    }
    pdf.setTextColor(0, 0, 0);

    y += 16;
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 6;

    // ── Two-column: Supplier | Buyer ──
    const colW = (pageWidth - margin * 2 - 8) / 2;
    const col2x = margin + colW + 8;

    const drawPartyBlock = (label: string, name: string, tin: string, idScheme: string, idVal: string,
        sst: string, addrLines: string[], city: string, postal: string,
        phone: string, email: string, msic: string, x: number, startY: number): number => {
        let py = startY;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(13, 110, 190);
        pdf.text(label.toUpperCase(), x, py);
        py += 4;
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        const nameLines = pdf.splitTextToSize(name, colW);
        nameLines.forEach((l: string) => { pdf.text(l, x, py); py += 4.5; });

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(60, 60, 60);
        pdf.text(`TIN: ${tin}`, x, py); py += 4;
        if (idVal && idVal !== 'N/A') { pdf.text(`${idScheme}: ${idVal}`, x, py); py += 4; }
        if (sst && sst !== 'N/A')     { pdf.text(`SST: ${sst}`, x, py); py += 4; }

        addrLines.forEach(l => { const wrapped = pdf.splitTextToSize(l, colW); wrapped.forEach((wl: string) => { pdf.text(wl, x, py); py += 4; }); });
        if (city || postal) { pdf.text(`${city} ${postal}`.trim(), x, py); py += 4; }
        if (phone) { pdf.text(`Tel: ${phone}`, x, py); py += 4; }
        if (email) { pdf.text(`Email: ${email}`, x, py); py += 4; }
        if (msic)  { const mw = pdf.splitTextToSize(`MSIC: ${msic}`, colW); mw.forEach((l: string) => { pdf.text(l, x, py); py += 4; }); }

        pdf.setTextColor(0, 0, 0);
        return py;
    };

    const sellerEndY = drawPartyBlock('Supplier', sellerName, sellerTin, 'BRN', sellerBrn,
        sellerSst, sellerLines, sellerCity, sellerPostal, sellerPhone, sellerEmail, sellerMsic, margin, y);
    const buyerEndY = drawPartyBlock('Buyer', buyerName, buyerTin, buyerIdScheme, buyerIdVal,
        buyerSst, buyerLines, buyerCity, buyerPostal, buyerPhone, buyerEmail, '', col2x, y);

    y = Math.max(sellerEndY, buyerEndY) + 4;
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 6;

    // ── Pagination helpers (consolidated invoices can have 100s of lines) ──
    // Reserve space so content never paints under the fixed footer band.
    const FOOTER_BAND = 16; // mm reserved at bottom of every page
    const contentBottom = () => pageHeight - FOOTER_BAND;

    const drawPageFooter = () => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text(
            'This is a computer-generated e-Invoice validated by Lembaga Hasil Dalam Negeri Malaysia (LHDN) via MyInvois.',
            margin, pageHeight - 10
        );
        pdf.text(
            'No physical signature is required. This document is legally valid upon LHDN validation.',
            margin, pageHeight - 6
        );
        pdf.setTextColor(0, 0, 0);
    };

    const drawContinuationHeader = () => {
        // Compact header on continuation pages so line items keep context.
        pdf.setFillColor(13, 110, 190);
        pdf.rect(0, 0, pageWidth, 10, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(255, 255, 255);
        pdf.text('LHDN MyInvois', margin, 6.5);
        const contLabel = `e-Invoice ${invoiceId} (continued)`;
        pdf.text(contLabel, pageWidth - margin - pdf.getTextWidth(contLabel), 6.5);
        pdf.setTextColor(0, 0, 0);
        return 18; // starting y for content on continuation pages
    };

    const amtColX = pageWidth - margin;   // shared right edge for line items + totals
    const descW = pageWidth - margin * 2 - 30 - 30 - 28;

    const drawTableHeader = (atY: number): number => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setFillColor(240, 245, 255);
        pdf.rect(margin, atY - 4, pageWidth - margin * 2, 7, 'F');
        pdf.setTextColor(0, 0, 0);
        pdf.text('Description', margin + 2, atY);
        pdf.text('Qty', margin + descW + 2, atY);
        pdf.text(`Unit Price (${currency})`, margin + descW + 32, atY, { align: 'right' });
        pdf.text(`Amount (${currency})`, amtColX, atY, { align: 'right' });
        let nextY = atY + 5;
        pdf.setLineWidth(0.2);
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, nextY, pageWidth - margin, nextY);
        return nextY + 4;
    };

    /** Start a new page, stamp footer on the finished page, redraw table header. */
    const newPageForItems = (): number => {
        drawPageFooter();
        pdf.addPage();
        const startY = drawContinuationHeader();
        return drawTableHeader(startY);
    };

    // ── Line items table ──
    y = drawTableHeader(y);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    for (const line of invoiceLines) {
        const desc      = g((line.Item as { Description?: UblArr }[])?.[0]?.Description) ||
                          g((line.Item as { Name?: UblArr }[])?.[0]?.Name) || '—';
        const qty       = gNum(line.InvoicedQuantity as UblArr);
        const unitPrice = gNum((line.Price as { PriceAmount?: UblArr }[])?.[0]?.PriceAmount);
        const lineAmt   = gNum(line.LineExtensionAmount as UblArr);
        const descLines = pdf.splitTextToSize(desc, descW) as string[];
        const rowH = Math.max(descLines.length * 4, 4) + 2;

        // If this row won't fit above the footer, continue on a new page.
        if (y + rowH > contentBottom()) {
            y = newPageForItems();
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
        }

        descLines.forEach((dl: string, i: number) => { pdf.text(dl, margin + 2, y + i * 4); });
        pdf.text(qty.toString(), margin + descW + 2, y);
        pdf.text(fmtAmt(unitPrice), margin + descW + 32, y, { align: 'right' });
        pdf.text(fmtAmt(lineAmt), amtColX, y, { align: 'right' });
        y += rowH;
    }

    // Ensure totals + QR fit above the footer; otherwise continue on a new page
    // (without a line-item table header — summary-only continuation).
    const totalsBlockH = 28;
    const qrBlockH = 40;
    if (y + totalsBlockH + qrBlockH > contentBottom()) {
        drawPageFooter();
        pdf.addPage();
        y = drawContinuationHeader();
    }

    y += 2;
    pdf.setLineWidth(0.3);
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 5;

    // ── Totals (right-aligned, same amtColX as line items) ──
    const totX = pageWidth - margin - 60;
    pdf.setFontSize(8);
    const drawTotalRow = (label: string, val: string, bold = false) => {
        pdf.setFont('helvetica', bold ? 'bold' : 'normal');
        pdf.setTextColor(0, 0, 0);
        pdf.text(label, totX, y);
        pdf.text(val, amtColX, y, { align: 'right' });
        y += 5;
    };
    drawTotalRow(`Subtotal (${currency})`, fmtAmt(subtotal));
    drawTotalRow(`Service Tax ${taxPct}% (${currency})`, fmtAmt(taxAmount));
    pdf.setDrawColor(13, 110, 190);
    pdf.setLineWidth(0.4);
    pdf.line(totX, y, amtColX, y);
    y += 4;
    drawTotalRow(`Total Payable (${currency})`, fmtAmt(total), true);

    y += 4;
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.line(margin, y, pageWidth - margin, y);
    y += 6;

    // If QR block still won't fit, push it to a fresh continuation page
    const qrSize = 32;
    if (y + qrSize + 8 > contentBottom()) {
        drawPageFooter();
        pdf.addPage();
        y = drawContinuationHeader();
    }

    // ── QR Code + validation info ──
    const qrX = pageWidth - margin - qrSize;
    pdf.addImage(qrDataUrl, 'PNG', qrX, y, qrSize, qrSize);

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(13, 110, 190);
    pdf.text('e-Invoice Validation', margin, y + 4);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(60, 60, 60);
    pdf.text('Scan QR or click the link below to verify this e-invoice on MyInvois:', margin, y + 9);
    // Avoid textWithLink + Unicode arrows — jsPDF/Helvetica often renders that as
    // wildly spaced glyphs ("C l i c k ..."). Draw plain text + an invisible link box.
    const verifyLabel = 'Click here to verify on MyInvois ->';
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(13, 110, 190);
    pdf.text(verifyLabel, margin, y + 14);
    const verifyW = pdf.getTextWidth(verifyLabel);
    pdf.link(margin, y + 14 - 3.5, verifyW, 5, { url: validationUrl });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(6);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`UUID: ${documentUuid}`, margin, y + 19);
    pdf.text(`Long ID: ${longId}`, margin, y + 23);

    pdf.setTextColor(0, 0, 0);

    // Footer on the final page
    drawPageFooter();

    return pdf.output('blob');
};

